import { Formik, Form, FormikHelpers } from "formik";
import { atom, useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { formDataAtom } from "../App";
import axios from "axios";
import { FOR_PATIENT } from "./MedicalTravel";
import { BY_BOTH, BY_PRIVATE, BY_PUBLIC } from "./TravelDetails";

import { useRef, useState } from "react";
import { MedicalTravelModel, MedicalVisit } from "../types/FormTypes";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

import { URLS } from "src/config/urls";

import {
  Box,
  CheckboxForm,
  Button,
  ErrorList,
  Spinner,
  Progressbar,
  handleSubmitClick,
  Icon,
  Heading,
} from "@ogt/ui";

interface SummaryModel {
  gdprAccept: boolean;
}
interface Issue {
  id: string;
}
enum VisitField {
  certificate,
  wayOfTravel,
  dateTo,
  dateFrom,
  travelDescription,
  receipts,
  otherFiles,
  comments,
}

export const issueAtom = atom<Issue>({
  id: "",
});

export default function Summary() {
  const [formData, setFormData] = useAtom(formDataAtom);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const navigate = useNavigate();
  const progress = 100;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIssueId] = useAtom(issueAtom);
  const formRef = useRef<HTMLFormElement>(null);

  function GetEditButtonForNavigation(path: string, text?: string) {
    return (
      <Button
        variant="ghost"
        showIcon
        iconName="square-pen"
        onClick={() => navigate(path)}
      >
        {text ?? "Gå tillbaka och ändra"}
      </Button>
    );
  }
  function GetDeleteButtonForMedicalVisit(index: number) {
    return (
      <div className="summary-remove">
        <Button
          type="button"
          variant={"ghost"}
          iconName="trash"
          showIcon
          iconPosition="left"
          onClick={() => {
            if (index < formData.medicalVisits.length) {
              formData.medicalVisits.splice(index, 1);
              setFormData({
                ...formData,
                medicalVisits: [...formData.medicalVisits],
              });
            }
          }}
        >
          Ta bort vårdbesök {index + 1}
        </Button>
      </div>
    );
  }
  const handleSubmit = (
    values: SummaryModel,
    actions: FormikHelpers<SummaryModel>
  ) => {

    if (isFormSubmitted) {
      return;
    }

    setIsFormSubmitted(true);

    const updatedFormData = {
      ...formData,
      patientssn: formData.patientssn.replace(/-/g, ""),
      caregiverssn: formData.caregiverssn.replace(/-/g, ""),
    };

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;


    const payload = {
      Patient: {
        Ssn: updatedFormData.patientssn,
        Firstname: updatedFormData.patientfirstname,
        Lastname: updatedFormData.patientlastname,
        Phone: updatedFormData.patientphone,
        Email: updatedFormData.patientemail,
      },

      Caregiver: {
        Ssn: updatedFormData.caregiverssn,
        Firstname: updatedFormData.caregiverfirstname,
        Lastname: updatedFormData.caregiverlastname,
        Phone: updatedFormData.caregiverphone,
        Email: updatedFormData.caregiveremail,
      },

      BankDetails: {
        ClearingNumber: updatedFormData.clearingNumber,
        AccountNumber: updatedFormData.accountNumber,
      },

      MedicalVisits: updatedFormData.medicalVisits.map((visit) => ({
        Certificate: visit.certificate.map((file) => ({
          Filename: file.file.name,
          Data: file.data,
        })),

        WayOfTravel: visit.wayOfTravel,

        Receipts: visit.receipts.map((file) => ({
          Filename: file.file.name,
          Data: file.data,
        })),

        TravelDescription: visit.travelDescription,
        Comments: visit.comments,
        FromDate: visit.dateFrom,
        ToDate: visit.dateTo,

        OtherFiles: visit.otherFiles.map((file) => ({
          Filename: file.file.name,
          Data: file.data,
        })),
      })),
    };

    axios
      .post(
        `${apiBaseUrl?.replace(/\/$/, "") ?? ""}/api/v1/Crm/MedicalTravelApplication`,
        payload,
        {
          headers: {
            "referrer-policy": "strict-origin",
          },
        }
      )
      .then((result) => {
        setIssueId({ id: result.data });
        setFormData({
          compensationfor: "",
          patientssn: "",
          patientfirstname: "",
          patientlastname: "",
          patientemail: "",
          patientphone: "",
          caregiverssn: "",
          caregiverfirstname: "",
          caregiverlastname: "",
          caregiveremail: "",
          caregiverphone: "",
          clearingNumber: "",
          accountNumber: "",
          medicalVisits: [],
        });
        navigate("/tack-sida");
      })
      .catch((error) => {
        navigate("../ett-fel-intraffade");
      });
  };
  return (
    <div>
      <Formik<SummaryModel>
        onSubmit={handleSubmit}
        initialValues={{
          gdprAccept: false,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          gdprAccept: Yup.bool().test(
            "checkbox-selected",
            "Du måste godkänna hanteringen av dina personuppgifter.",
            (value) => {
              return value === true;
            }
          ),
          ...formValidation(formData),
        })}
        enableReinitialize
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form ref={formRef}>
              <Box fullwidth>
                {isFormSubmitted && <Spinner label="Skickar ..." />}

                <div
                  className={`inner-summary ${isFormSubmitted === true ? "hide" : ""
                    }`}
                >
                  <Heading progress={progress} headingRef={h2Ref} focusable>
                    Granska och skicka in ansökan
                  </Heading>

                  <ErrorList errors={errors} />

                  <p>
                    Granska dina uppgifter innan du skickar in ansökan. Vill du
                    ändra något klickar du på "Ändra". Då kommer du till sidan i
                    formuläret där du kan ändra. Klicka sedan på "Gå vidare" för
                    att göra klart ansökan.
                  </p>

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Patient
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <div className="summary-data-label font-bold">
                            Patientens personnummer
                          </div>
                          <div className="summary-data">
                            {formData.patientssn}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <div className="summary-data-label font-bold">
                            Patientens namn
                          </div>
                          <div className="summary-data">
                            {formData.patientfirstname +
                              " " +
                              formData.patientlastname}
                          </div>
                        </div>

                        {formData.patientemail && (
                          <div className="flex gap-2">
                            <div className="summary-data-label font-bold">
                              Patientens epost
                            </div>
                            <div className="summary-data break-all">
                              {formData.patientemail}
                            </div>
                          </div>
                        )}

                        {formData.patientphone && (
                          <div className="flex gap-2">
                            <div className="summary-data-label font-bold">
                              Patientens mobilnummer
                            </div>
                            <div className="summary-data break-all">
                              {formData.patientphone}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation("../")}
                        </div>
                      </div>
                    </div>

                    {HasCareGiverData(formData) && (
                      <>
                        <div className="flex md:justify-between flex-col md:flex-row mt-6 mb-2">
                          <Heading as="h3" className="summary-section-heading">
                            Vårdnadshavare
                          </Heading>
                        </div>

                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <div className="summary-data-label font-bold">
                                Vårdnadshavarens personnummer
                              </div>
                              <div className="summary-data">
                                {formData.caregiverssn}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <div className="summary-data-label font-bold">
                                Vårdnadshavarens namn
                              </div>
                              <div className="summary-data">
                                {formData.caregiverfirstname +
                                  " " +
                                  formData.caregiverlastname}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <div className="summary-data-label font-bold">
                                Vårdnadshavarens epost
                              </div>
                              <div className="summary-data break-all">
                                {formData.caregiveremail}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <div className="summary-data-label font-bold">
                                Vårdnadshavarens mobilnummer
                              </div>
                              <div className="summary-data break-all">
                                {formData.caregiverphone}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <hr />

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Kontouppgifter för utbetalning
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <div className="summary-data-label font-bold">
                            Clearing
                          </div>
                          <div className="summary-data">
                            {formData.clearingNumber}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <div className="summary-data-label font-bold">
                            Kontonummer
                          </div>
                          <div className="summary-data">
                            {formData.accountNumber}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation("../utbetalning")}
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />
                  {formData.medicalVisits.map((visit, idx) => (
                    <div
                      className={`summary-section visit${idx}-summary`}
                      key={idx}
                    >
                      <div className="flex md:justify-between flex-col md:flex-row mb-2">
                        <Heading as="h3" className="summary-section-heading">
                          Vårdbesök {idx + 1}
                        </Heading>
                      </div>

                      <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <div
                              className={GetLabelClass(
                                visit,
                                VisitField.certificate
                              )}
                            >
                              Bifogat intyg
                            </div>
                            <div
                              className={GetDataClass(
                                visit,
                                VisitField.certificate
                              )}
                            >
                              {visit.certificate.map(
                                (doc) => doc.file.name + " "
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <div
                              className={GetLabelClass(
                                visit,
                                VisitField.wayOfTravel
                              )}
                            >
                              Hur reste du till/från kliniken?
                            </div>
                            <div
                              className={GetDataClass(
                                visit,
                                VisitField.wayOfTravel
                              )}
                            >
                              {GetWayOfTravelReadable(visit.wayOfTravel)}
                            </div>
                          </div>

                          {visit.dateTo && (
                            <div className="flex gap-2">
                              <div
                                className={GetLabelClass(
                                  visit,
                                  VisitField.dateTo
                                )}
                              >
                                Datum till vårdenhet
                              </div>
                              <div
                                className={GetDataClass(
                                  visit,
                                  VisitField.dateTo
                                )}
                              >
                                {visit.dateTo}
                              </div>
                            </div>
                          )}

                          {visit.dateFrom && (
                            <div className="flex gap-2">
                              <div
                                className={GetLabelClass(
                                  visit,
                                  VisitField.dateFrom
                                )}
                              >
                                Datum från vårdenhet
                              </div>
                              <div
                                className={GetDataClass(
                                  visit,
                                  VisitField.dateFrom
                                )}
                              >
                                {visit.dateFrom}
                              </div>
                            </div>
                          )}

                          {visit.receipts.length > 0 && (
                            <div className="flex gap-2">
                              <div
                                className={GetLabelClass(
                                  visit,
                                  VisitField.receipts
                                )}
                              >
                                Bifogade biljetter
                              </div>
                              <div
                                className={GetDataClass(
                                  visit,
                                  VisitField.receipts
                                )}
                              >
                                {visit.receipts.map(
                                  (doc) => doc.file.name + " "
                                )}
                              </div>
                            </div>
                          )}

                          {visit.travelDescription && (
                            <div className="flex gap-2">
                              <div
                                className={GetLabelClass(
                                  visit,
                                  VisitField.travelDescription
                                )}
                              >
                                Berätta om sträckan som du åkte med bil
                              </div>
                              <div
                                className={GetDataClass(
                                  visit,
                                  VisitField.travelDescription
                                )}
                              >
                                {visit.travelDescription}
                              </div>
                            </div>
                          )}

                          {visit.otherFiles.length > 0 && (
                            <div className="flex gap-2">
                              <div
                                className={GetLabelClass(
                                  visit,
                                  VisitField.otherFiles
                                )}
                              >
                                Övriga dokument
                              </div>
                              <div
                                className={GetDataClass(
                                  visit,
                                  VisitField.otherFiles
                                )}
                              >
                                {visit.otherFiles.map(
                                  (doc) => doc.file.name + " "
                                )}
                              </div>
                            </div>
                          )}

                          {visit.comments && (
                            <div className="flex gap-2">
                              <div
                                className={GetLabelClass(
                                  visit,
                                  VisitField.comments
                                )}
                              >
                                Övriga kommentarer
                              </div>
                              <div
                                className={GetDataClass(
                                  visit,
                                  VisitField.comments
                                )}
                              >
                                {visit.comments}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col">
                          <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                            {formData.medicalVisits.length > 1
                              ? GetDeleteButtonForMedicalVisit(idx)
                              : null}
                          </div>
                        </div>
                      </div>

                      <hr />
                    </div>
                  ))}
                  <div className="mt-6 mb-6 md:mb-8">
                    <fieldset className="border-0 p-0 m-0">
                      <legend className="sr-only">
                        Godkännande av personuppgifter
                      </legend>
                      <CheckboxForm width="fullWidth" name="gdprAccept">
                        Jag godkänner att Östgötatrafiken sparar och hanterar
                        mina personuppgifter enligt{" "}
                        <a
                          className="flex"
                          target="_blank"
                          rel="noreferrer"
                          href={URLS.privacyPolicy}
                        >
                          relevant dataskyddslagstiftning.
                          <Icon
                            className="inline-block ml-1"
                            name="external-link"
                            size={12}
                          />
                        </a>
                      </CheckboxForm>
                    </fieldset>
                  </div>
                </div>
              </Box>

              <div
                className={`flex justify-centery ${isFormSubmitted === true ? "hide" : ""
                  }`}
              >
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    type="submit"
                    disabled={isFormSubmitted}
                    onClick={handleSubmitClick(formRef)}
                  >
                    {isFormSubmitted ? (
                      <>
                        <Icon
                          name="loader-circle"
                          className="inline-block size-5 animate-spin"
                          aria-hidden="true"
                        />
                        Skickar...
                      </>
                    ) : (
                      "Skicka ansökan"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}

function GetWayOfTravelReadable(wayOfTravel: string): string {
  return wayOfTravel === BY_PRIVATE
    ? "Privatbil"
    : wayOfTravel === BY_PUBLIC
      ? "Kollektivtrafik"
      : "Privatbil och kollektivtrafik";
}

function HasCareGiverData(data: MedicalTravelModel): boolean {
  return (
    (data.caregiverssn ||
      data.caregiverfirstname ||
      data.caregiverlastname ||
      data.caregiveremail) !== ""
  );
}
function formValidation(data: MedicalTravelModel): any {
  let valObj = {
    patient: Yup.bool().test(
      "form-filled-out",
      "Det saknas obligatoriska uppgifter för Patient.",
      () => {
        return isPatientFilledOut(data);
      }
    ),
    caregiver: Yup.bool().test(
      "form-filled-out",
      "Det saknas obligatoriska uppgifter för Vårdnadshavare.",
      () => {
        return (
          data.compensationfor !== FOR_PATIENT || isCaregiverFilledOut(data)
        );
      }
    ),
  } as any;
  data.medicalVisits.forEach((visit, index) => {
    valObj[`visit${index}`] = Yup.bool().test(
      "form-filled-out",
      `Det saknas obligatoriska uppgifter för Vårdbesök ${index + 1}.`,
      () => {
        return isVisitFilledOut(visit);
      }
    );
  });
  return valObj;
}
function isPatientFilledOut(data: MedicalTravelModel): boolean {
  return (
    (data.patientssn && data.patientfirstname && data.patientlastname) !== "" &&
    (data.compensationfor === FOR_PATIENT || data.patientemail !== "")
  );
}
function isCaregiverFilledOut(data: MedicalTravelModel): boolean {
  return (
    (data.caregiverssn &&
      data.caregiverfirstname &&
      data.caregiverlastname &&
      data.caregiveremail) !== ""
  );
}
function isVisitFilledOut(visit: MedicalVisit): boolean {
  return (
    visit.certificate.length === 1 &&
    (visit.wayOfTravel === BY_PUBLIC ||
      (visit.dateFrom && visit.dateTo) !== "") &&
    (visit.wayOfTravel === BY_PRIVATE || visit.receipts.length > 0) &&
    (visit.wayOfTravel !== BY_BOTH || visit.travelDescription !== "")
  );
}
function IsLastData(data: MedicalVisit, field: VisitField): boolean {
  if (field === VisitField.comments) {
    return true;
  }
  if (field === VisitField.otherFiles) {
    return data.comments.length === 0;
  }
  if (field === VisitField.travelDescription) {
    return data.otherFiles.length + data.comments.length === 0;
  }
  if (field === VisitField.receipts) {
    return (
      data.travelDescription.length +
      data.otherFiles.length +
      data.comments.length +
      data.dateFrom.length ===
      0
    );
  }
  return false;
}
function GetLabelClass(visit: MedicalVisit, field: VisitField): string {
  const label = "summary-data-label font-bold";
  if (IsLastData(visit, field)) return label + " last";
  return label;
}
function GetDataClass(visit: MedicalVisit, field: VisitField): string {
  const label = "summary-data";
  if (IsLastData(visit, field)) return label + " last";
  return label;
}
